/**
 * useClipboardWithFeedback — single source of truth for "Copy → show 'Copied!'
 * for N seconds → reset" pattern that's duplicated 22 times across the codebase.
 *
 * Replaces this pattern:
 *
 *   const [copied, setCopied] = useState(false);
 *   const copy = () => {
 *     navigator.clipboard.writeText(text).then(() => {
 *       setCopied(true);
 *       setTimeout(() => setCopied(false), 2000);
 *     });
 *   };
 *
 * With:
 *
 *   const { copy, copied } = useClipboardWithFeedback({ text });
 *   <button onClick={copy}>{copied ? "Copied!" : "Copy"}</button>
 *
 * Or for buttons that pass the text dynamically:
 *
 *   const { copy, copied } = useClipboardWithFeedback();
 *   <button onClick={() => copy("dynamic text")}>{copied ? "Copied!" : "Copy"}</button>
 *
 * Features the previous inline pattern lacks:
 *  - Auto-clears the timeout on unmount (prevents "can't update unmounted
 *    component" warnings)
 *  - Optional `onSuccess` callback for analytics / toasts
 *  - Optional `onError` callback that fires when clipboard write rejects
 *    (older browsers, no HTTPS, permissions denied)
 *  - Customizable `resetMs` (defaults to 2000)
 *  - SSR-safe (no navigator.clipboard reference until copy is called)
 *
 * Audit reference: code-health audit 2026-05-13, item #5 (HIGH).
 */

import { useCallback, useEffect, useRef, useState } from "react";

interface Options {
  /** Static text to copy. If omitted, pass text to `copy(text)` at call time. */
  text?: string;
  /** Milliseconds to keep `copied=true` before auto-resetting. Default 2000. */
  resetMs?: number;
  /** Called after a successful clipboard write. */
  onSuccess?: (text: string) => void;
  /** Called when clipboard write fails (rare; legacy browsers, no HTTPS, etc). */
  onError?: (err: unknown) => void;
}

export function useClipboardWithFeedback(options: Options = {}) {
  const { text: staticText, resetMs = 2000, onSuccess, onError } = options;
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear any pending reset timeout on unmount so React doesn't fire
  // setCopied on an unmounted component.
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const copy = useCallback(
    async (overrideText?: string) => {
      const value = overrideText ?? staticText ?? "";
      if (!value) return;

      try {
        // Modern path — async clipboard API (HTTPS + recent browsers).
        if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value);
        } else {
          // Legacy fallback for non-HTTPS or very old browsers.
          // Uses execCommand which is deprecated but still widely supported.
          const ta = document.createElement("textarea");
          ta.value = value;
          ta.style.position = "fixed";
          ta.style.left = "-9999px";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }

        setCopied(true);
        onSuccess?.(value);

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setCopied(false), resetMs);
      } catch (err) {
        onError?.(err);
        // Don't throw — caller can opt into error handling via onError.
      }
    },
    [staticText, resetMs, onSuccess, onError],
  );

  return { copy, copied };
}

export default useClipboardWithFeedback;
